// Plane geometry for the geofence editor (geofence_editor_controller.js), in the same
// equirectangular projection Geo::Geofence uses on the server: metres east/north of the
// polygon's first corner. Good to well under a metre over the few hundred metres a site spans.
// Points are { lat, lng }.

export const METERS_PER_DEGREE = 111_195

function projector(origin) {
  const cos = Math.cos((origin.lat * Math.PI) / 180)
  return ({ lat, lng }) => [
    (lng - origin.lng) * METERS_PER_DEGREE * cos,
    (lat - origin.lat) * METERS_PER_DEGREE,
  ]
}

// Area of the polygon the points enclose (the ring closes itself), in m².
export function polygonArea(points) {
  if (points.length < 3) return 0
  const ring = points.map(projector(points[0]))
  const twice = ring.reduce((sum, [x1, y1], index) => {
    const [x0, y0] = ring[(index + ring.length - 1) % ring.length]
    return sum + (x0 * y1 - x1 * y0)
  }, 0)
  return Math.abs(twice) / 2
}

// true when two edges that share no corner cross or touch — the server refuses such a ring.
export function selfIntersecting(points) {
  if (points.length < 4) return false
  const ring = points.map(projector(points[0]))
  const edges = ring.map((point, index) => [point, ring[(index + 1) % ring.length]])
  return edges.some((edge, i) =>
    edges.some((other, j) => {
      if (j <= i + 1) return false
      if (i === 0 && j === edges.length - 1) return false
      return segmentsIntersect(edge[0], edge[1], other[0], other[1])
    }),
  )
}

function orientation(a, b, c) {
  const value = (b[1] - a[1]) * (c[0] - b[0]) - (b[0] - a[0]) * (c[1] - b[1])
  if (Math.abs(value) < 1e-9) return 0
  return value > 0 ? 1 : 2
}

function onSegment(a, b, c) {
  return (
    Math.min(a[0], c[0]) <= b[0] &&
    b[0] <= Math.max(a[0], c[0]) &&
    Math.min(a[1], c[1]) <= b[1] &&
    b[1] <= Math.max(a[1], c[1])
  )
}

function segmentsIntersect(p1, q1, p2, q2) {
  const o1 = orientation(p1, q1, p2)
  const o2 = orientation(p1, q1, q2)
  const o3 = orientation(p2, q2, p1)
  const o4 = orientation(p2, q2, q1)
  if (o1 !== o2 && o3 !== o4) return true
  return (
    (o1 === 0 && onSegment(p1, p2, q1)) ||
    (o2 === 0 && onSegment(p1, q2, q1)) ||
    (o3 === 0 && onSegment(p2, p1, q2)) ||
    (o4 === 0 && onSegment(p2, q1, q2))
  )
}

// A regular polygon of +corners+ corners on the circle — where a polygon starts when the
// editor switches from the circle, so there is a shape to drag rather than an empty map.
export function circleToPolygon(center, radiusM, corners = 8) {
  const latRadius = radiusM / METERS_PER_DEGREE
  const lngRadius = latRadius / Math.cos((center.lat * Math.PI) / 180)
  return Array.from({ length: corners }, (_, index) => {
    const angle = (2 * Math.PI * index) / corners
    return {
      lat: round(center.lat + latRadius * Math.cos(angle)),
      lng: round(center.lng + lngRadius * Math.sin(angle)),
    }
  })
}

export function midpoint(a, b) {
  return { lat: round((a.lat + b.lat) / 2), lng: round((a.lng + b.lng) / 2) }
}

// The 7 decimals the server stores (about a centimetre).
export function round(value) {
  return Math.round(value * 1e7) / 1e7
}
